import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useChatNotifications,
  type ChatNotification,
} from "@/context/ChatNotificationsContext";

const COMMENT_TYPE_LABEL: Record<string, string> = {
  PUBLIC: "Comentário público",
  CITIZEN_RESPONSE: "Resposta do cidadão",
  INTERNAL: "Comentário interno",
  AGENT_RESPONSE: "Resposta do agente",
};

const COMMENT_TYPE_COLOR: Record<
  string,
  { bg: string; text: string; badge: string }
> = {
  PUBLIC: {
    bg: "bg-emerald-500",
    text: "text-emerald-700",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  CITIZEN_RESPONSE: {
    bg: "bg-blue-500",
    text: "text-blue-700",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
  },
  INTERNAL: {
    bg: "bg-purple-500",
    text: "text-purple-700",
    badge: "bg-purple-50 text-purple-700 border-purple-200",
  },
  AGENT_RESPONSE: {
    bg: "bg-orange-500",
    text: "text-orange-700",
    badge: "bg-orange-50 text-orange-700 border-orange-200",
  },
};

const DEFAULT_COLOR = {
  bg: "bg-gray-400",
  text: "text-gray-700",
  badge: "bg-gray-50 text-gray-700 border-gray-200",
};

interface NotificationItemProps {
  notification: ChatNotification;
  onDismiss: (id: string) => void;
  onNavigate: (protocol: string, id: string) => void;
}

const NotificationItem = ({
  notification: n,
  onDismiss,
  onNavigate,
}: NotificationItemProps) => {
  const color = COMMENT_TYPE_COLOR[n.commentType] ?? DEFAULT_COLOR;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 110, scale: 0.96 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden w-full"
    >
      {/* accent stripe */}
      <div className={`h-1 w-full ${color.bg}`} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`p-1.5 rounded-full bg-opacity-10 ${color.bg.replace("bg-", "bg-")}/10`}
            >
              <MessageSquare className={`h-3.5 w-3.5 ${color.text}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900 leading-tight truncate">
                Nova mensagem
              </p>
              <p className="text-[11px] text-gray-500 truncate">
                {n.authorName}
              </p>
            </div>
          </div>
          <button
            onClick={() => onDismiss(n.id)}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors shrink-0 cursor-pointer"
            aria-label="Fechar notificação"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <span className="text-[10px] font-mono font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200">
            #{n.protocol}
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded border ${color.badge}`}
          >
            {COMMENT_TYPE_LABEL[n.commentType] ?? n.commentType}
          </span>
        </div>

        {/* Message preview */}
        <p className="text-xs text-gray-600 line-clamp-2 mb-3 leading-relaxed">
          {n.messagePreview}
        </p>

        {/* CTA */}
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs w-full cursor-pointer hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
          onClick={() => onNavigate(n.protocol, n.id)}
        >
          <ExternalLink className="h-3 w-3 mr-1.5" />
          Ver conversa
        </Button>
      </div>
    </motion.div>
  );
};

export const ChatNotificationPanel = () => {
  const { notifications, dismissNotification, dismissAll } =
    useChatNotifications();
  const navigate = useNavigate();

  if (notifications.length === 0) return null;

  const handleNavigate = (protocol: string, notificationId: string) => {
    dismissNotification(notificationId);
    // Deeplink para a tela de detalhes da ocorrência (possui o chat)
    navigate(`/occurrences/${protocol}`);
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[9998] flex flex-col-reverse gap-2.5 max-w-[340px] w-full pointer-events-none"
      aria-live="polite"
      aria-label="Notificações de mensagens"
    >
      {notifications.length > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-end pointer-events-auto"
        >
          <button
            onClick={dismissAll}
            className="text-[10px] text-gray-500 hover:text-gray-700 bg-white/95 border border-gray-200 px-2.5 py-1 rounded-full shadow-sm cursor-pointer transition-colors"
          >
            Fechar todas ({notifications.length})
          </button>
        </motion.div>
      )}

      <div className="flex flex-col gap-2.5 pointer-events-auto">
        <AnimatePresence mode="popLayout">
          {notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onDismiss={dismissNotification}
              onNavigate={handleNavigate}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
